export default function filtersComponent() {
    return {
        activeFilters: [],
        isCumulative: false,
        animating: false,
        expandedParents: new Set(),
        filterHierarchy: {},
        hasVisibleItems: true,
        query: '',
        viewMode: 'grid',
        sortMode: 'featured',
        outsideTypologies: false,
        dropdownOpen: '',
        searchOpen: false,
        selectedTypology: 'all',
        selectedTypologyLabel: 'All',
        selectedSubTypology: '',
        selectedSubTypologyLabel: 'All',
        selectedStatus: '',
        selectedLocation: '',
        statusOptions: [],
        locationOptions: [],
        filterLabels: {},
        childTypologyOptionsByParent: {},
        initialDiscoveryState: null,
        visibleCount: 0,
        cachedElements: new Map(),
        parentMap: {},
        
        init() {
            const { dataset } = this.$el;
            
            // Initialize from URL parameters or dataset
            this.initializeFromURL(dataset);
            this.buildFilterHierarchy();
            this.buildFilterLabels();
            this.buildChildTypologyOptions();
            this.cacheElements();
            this.syncTypologyFromActiveFilters();
            this.hydrateFacetOptions();
            this.captureInitialDiscoveryState();
            
            // Batch initial updates
            requestAnimationFrame(() => {
                this.updateActiveFilterClasses();
                this.updateExpandedStates();
                this.applyDiscovery();
                
                // Defer non-critical operations
                setTimeout(() => {
                    this.applyDiscovery();
                }, 50);
            });
            
            this.setupListeners();
        },
        
        initializeFromURL(dataset) {
            const shouldRestoreState = dataset.restoreState === '1';
            const urlParams = new URLSearchParams(window.location.search);
            const urlFilters = urlParams.get('filters');
            const urlExpanded = urlParams.get('expanded');
            const urlCumulative = urlParams.get('cumulative');
            const urlQuery = urlParams.get('q');
            const urlView = urlParams.get('view');
            const urlSort = urlParams.get('sort');
            const urlOutside = urlParams.get('outside');
            const urlStatus = urlParams.get('status');
            const urlLocation = urlParams.get('location');

            // Decode %2C to comma for correct splitting
            const decodedFilters = urlFilters ? urlFilters.replace(/%2C/gi, ',') : null;
            const decodedExpanded = urlExpanded ? urlExpanded.replace(/%2C/gi, ',') : null;

            // Use URL parameters if available, otherwise fall back to dataset
            if (shouldRestoreState && decodedFilters) {
                this.activeFilters = decodedFilters.split(',');
            } else {
                this.activeFilters = dataset.initialFilter?.trim()?.split(/\s+/) || [];
            }

            // Restore expanded state from URL
            if (shouldRestoreState && decodedExpanded) {
                this.expandedParents = new Set(decodedExpanded.split(','));
            }

            // Restore cumulative mode from URL
            this.isCumulative = (shouldRestoreState && urlCumulative === '1') || dataset.cumulative === "true";
            this.query = shouldRestoreState ? (urlQuery || '').trim() : '';
            this.viewMode = shouldRestoreState && ['grid', 'list'].includes(urlView) ? urlView : 'grid';
            if (shouldRestoreState) {
                const normalizedSort = urlSort === 'date' ? 'date_desc' : urlSort;
                this.sortMode = ['featured', 'date_desc', 'date_asc', 'az'].includes(normalizedSort) ? normalizedSort : 'featured';
            } else {
                this.sortMode = 'featured';
            }
            this.outsideTypologies = shouldRestoreState && urlOutside === '1';
            this.selectedStatus = shouldRestoreState ? (urlStatus || '').trim() : '';
            this.selectedLocation = shouldRestoreState ? (urlLocation || '').trim() : '';
        },
        
        cacheElements() {
            // Cache frequently accessed elements for better performance
            this.cachedElements.set('wrapper', this.$refs.wrapper);
            this.cachedElements.set('filters', this.$el.querySelectorAll('.filter'));
            this.cachedElements.set('filterParents', this.$el.querySelectorAll('.filter-parent'));
            this.cachedElements.set('filterChildren', this.$el.querySelectorAll('.filter-children'));
            const items = this.getDiscoveryItems();
            items.forEach((item, index) => {
                if (!item.dataset.originalIndex) {
                    item.dataset.originalIndex = String(index);
                }
            });
        },
        
        setupListeners() {
            // Use passive listeners for better performance
            document.addEventListener('keydown', (e) => {
                if (e.key !== 'Escape') return;
                if (this.dropdownOpen) {
                    this.dropdownOpen = '';
                }
                if (this.searchOpen) {
                    this.searchOpen = false;
                }
                if (this.expandedParents.size > 0) {
                    this.expandedParents.clear();
                    this.updateExpandedStates();
                }
            }, { passive: true });

            // Native fallback bindings for select controls in case Alpine inline handlers are skipped.
            const typologySelect = this.$el.querySelector('[id^="typology-filter-"]');
            if (typologySelect && !typologySelect.dataset.boundDiscovery) {
                typologySelect.dataset.boundDiscovery = '1';
                typologySelect.addEventListener('change', (event) => {
                    this.setTypologyFilter(event.target.value || 'all');
                });
            }

            const subTypologySelect = this.$el.querySelector('[id^="subtypology-filter-"]');
            if (subTypologySelect && !subTypologySelect.dataset.boundDiscovery) {
                subTypologySelect.dataset.boundDiscovery = '1';
                subTypologySelect.addEventListener('change', (event) => {
                    this.setSubTypologyFilter(event.target.value || '');
                });
            }

            // Fallback for custom dropdown button options (status / location)
            this.$el.addEventListener('click', (event) => {
                const target = event.target instanceof Element ? event.target : null;
                if (!target) return;

                const statusOption = target.closest('[data-status-value]');
                if (statusOption && this.$el.contains(statusOption)) {
                    this.selectStatusOption(statusOption.getAttribute('data-status-value') || '');
                    return;
                }

                const locationOption = target.closest('[data-location-value]');
                if (locationOption && this.$el.contains(locationOption)) {
                    this.selectLocationOption(locationOption.getAttribute('data-location-value') || '');
                }
            });
        },

        toggleDropdown(name) {
            this.dropdownOpen = this.dropdownOpen === name ? '' : name;
        },

        closeDropdown(name = '') {
            if (!name || this.dropdownOpen === name) {
                this.dropdownOpen = '';
            }
        },

        toggleSearch() {
            this.searchOpen = !(this.searchOpen || this.query.length > 0);
            if (this.searchOpen) {
                requestAnimationFrame(() => {
                    this.$refs?.inlineSearchInput?.focus();
                });
            }
        },
        
        buildFilterHierarchy() {
            const hierarchyData = this.$el.dataset.filterHierarchy;

            if (hierarchyData) {
                try {
                    const cleanedData = hierarchyData
                        .trim()
                        .replace(/,\s*]/g, ']')
                        .replace(/,\s*}/g, '}');

                    this.filterHierarchy = JSON.parse(cleanedData);
                } catch (e) {
                    this.filterHierarchy = {};
                }
            } else {
                this.filterHierarchy = {};
            }
            // Build reverse map child -> parent for quick lookup
            this.parentMap = {};
            Object.keys(this.filterHierarchy).forEach(parent => {
                (this.filterHierarchy[parent] || []).forEach(child => {
                    this.parentMap[child] = parent;
                });
            });
        },

        buildFilterLabels() {
            const labelsData = this.$el.dataset.filterLabels;
            if (!labelsData) {
                this.filterLabels = {};
                return;
            }

            try {
                const cleanedData = labelsData
                    .trim()
                    .replace(/,\s*}/g, '}');
                this.filterLabels = JSON.parse(cleanedData);
            } catch (e) {
                this.filterLabels = {};
            }
        },

        buildChildTypologyOptions() {
            const childrenData = this.$el.dataset.filterChildren;
            if (!childrenData) {
                this.childTypologyOptionsByParent = {};
                return;
            }

            try {
                const cleanedData = childrenData
                    .trim()
                    .replace(/,\s*]/g, ']')
                    .replace(/,\s*}/g, '}');
                const parsed = JSON.parse(cleanedData);
                this.childTypologyOptionsByParent = parsed && typeof parsed === 'object' ? parsed : {};
            } catch (e) {
                this.childTypologyOptionsByParent = {};
            }
        },
        
        updateURLState() {
            const url = new URL(window.location);
            
            // Clear existing filter parameters
            url.searchParams.delete('filters');
            url.searchParams.delete('expanded');
            url.searchParams.delete('cumulative');
            url.searchParams.delete('q');
            url.searchParams.delete('view');
            url.searchParams.delete('sort');
            url.searchParams.delete('outside');
            url.searchParams.delete('status');
            url.searchParams.delete('location');
            
            // Add current filter state
            if (this.activeFilters.length > 0 && !this.activeFilters.includes('all')) {
                url.searchParams.set('filters', this.activeFilters.join(','));
            }
            
            if (this.expandedParents.size > 0) {
                url.searchParams.set('expanded', Array.from(this.expandedParents).join(','));
            }
            
            if (this.isCumulative) {
                url.searchParams.set('cumulative', '1');
            }

            if (this.query.trim()) {
                url.searchParams.set('q', this.query.trim());
            }

            if (this.viewMode !== 'grid') {
                url.searchParams.set('view', this.viewMode);
            }

            if (this.sortMode !== 'featured') {
                url.searchParams.set('sort', this.sortMode);
            }

            if (this.outsideTypologies) {
                url.searchParams.set('outside', '1');
            }

            if (this.selectedStatus) {
                url.searchParams.set('status', this.selectedStatus);
            }

            if (this.selectedLocation) {
                url.searchParams.set('location', this.selectedLocation);
            }
            
            // Update URL without triggering page reload
            const newUrl = url.searchParams.toString() ? 
                `${url.pathname}?${url.searchParams.toString()}` : 
                url.pathname;
                
            window.history.replaceState({}, '', newUrl);
        },
        
        async setFilter(filter, isChild = false, parentFilter = null) {
            if (this.animating) return;
            
            this.animating = true;
            
            const wrapper = this.cachedElements.get('wrapper');
            if (!wrapper) {
                this.animating = false;
                return;
            }
            
            // Optimized animation with CSS classes
            wrapper.classList.add('fade-slide-out');
            
            await this.delay(200);
            
            // Update filters while hidden
            if (filter === "all") {
                this.activeFilters = ["all"];
                this.expandedParents.clear();
            } else if (isChild) {
                this.handleChildFilter(filter, parentFilter);
            } else {
                this.handleParentFilter(filter);
            }
            
            // Batch DOM updates
            requestAnimationFrame(() => {
                this.updateActiveFilterClasses();
                this.updateExpandedStates();
                this.applyDiscovery();
                
                // Animate back in
                wrapper.classList.remove('fade-slide-out');
                wrapper.classList.add('fade-slide-in');
                
                setTimeout(() => {
                    wrapper.classList.remove('fade-slide-in');
                    this.animating = false;
                }, 250);
            });

            this.syncTypologyFromActiveFilters();
        },
        
        delay(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        },
        
        handleParentFilter(filter) {
            const hasChildren = this.filterHierarchy[filter]?.length > 0;
            
            // Remove "all" from activeFilters
            this.activeFilters = this.activeFilters.filter(f => f !== "all");
            
            if (hasChildren) {
                if (this.expandedParents.has(filter)) {
                    // Collapse
                    this.expandedParents.delete(filter);
                    this.activeFilters = this.activeFilters.filter(f => {
                        return f !== filter && !this.filterHierarchy[filter]?.includes(f);
                    });
                } else {
                    // Expand
                    this.clearOtherParentFilters(filter);
                    this.expandedParents.clear();
                    this.expandedParents.add(filter);
                    this.activeFilters.push(filter);
                }
            } else {
                // Regular filter without children
                this.expandedParents.clear();
                
                if (this.isCumulative) {
                    const index = this.activeFilters.indexOf(filter);
                    if (index > -1) {
                        this.activeFilters.splice(index, 1);
                    } else {
                        this.activeFilters.push(filter);
                    }
                } else {
                    this.activeFilters = [filter];
                }
            }
            
            // Fallback to "all" if no filters active
            if (this.activeFilters.length === 0 && this.expandedParents.size === 0) {
                this.activeFilters = ["all"];
            }
        },
        
        clearOtherParentFilters(currentFilter) {
            const allOtherParentFilters = [];
            Object.entries(this.filterHierarchy).forEach(([parentFilter, children]) => {
                if (parentFilter !== currentFilter) {
                    allOtherParentFilters.push(parentFilter, ...children);
                }
            });
            this.activeFilters = this.activeFilters.filter(f => !allOtherParentFilters.includes(f));
        },
        
        handleChildFilter(filter, parentFilter) {
            if (parentFilter) {
                this.expandedParents.add(parentFilter);
            }
            
            if (this.isCumulative) {
                const index = this.activeFilters.indexOf(filter);
                if (index > -1) {
                    this.activeFilters.splice(index, 1);
                } else {
                    this.activeFilters = [...this.activeFilters.filter(f => f !== "all"), filter];
                }
            } else {
                this.activeFilters = [filter];
            }
            
            if (this.activeFilters.length === 0) {
                this.activeFilters = ["all"];
                this.expandedParents.clear();
            }
        },
        
        isParentExpanded(filter) {
            return this.expandedParents.has(filter)
                || this.activeFilters.includes(filter)
                || this.hasActiveChildren(filter);
        },
        
        isVisible(filterClasses) {
            if (this.activeFilters.includes("all")) {
                return true;
            }
            
            if (!filterClasses || filterClasses.length === 0) {
                return false;
            }
            
            // FIXED: Convert string to array and trim for exact matching
            const filterArray = typeof filterClasses === 'string' 
                ? filterClasses.trim().split(/\s+/).filter(f => f.length > 0)
                : Array.isArray(filterClasses) 
                    ? filterClasses 
                    : [];
            
            if (filterArray.length === 0) {
                return false;
            }
            
            // Check for exact matches in active filters
            for (const activeFilter of this.activeFilters) {
                // Direct match
                if (filterArray.includes(activeFilter)) {
                    return true;
                }
                
                // Check if any children of this parent filter match
                const childFilters = this.filterHierarchy[activeFilter] || [];
                if (childFilters.some(child => filterArray.includes(child))) {
                    return true;
                }
            }
            
            return false;
        },

        isVisibleByDiscovery(filterClasses, element = null) {
            const filterArray = typeof filterClasses === 'string'
                ? filterClasses.trim().split(/\s+/).filter(f => f.length > 0)
                : Array.isArray(filterClasses)
                    ? filterClasses
                    : [];

            const typologyVisible = this.matchesSelectedTypology(filterArray);
            const filterVisible = this.outsideTypologies
                ? (typologyVisible ? false : this.isOutsideTypology(filterArray))
                : typologyVisible;

            if (!filterVisible) {
                return false;
            }

            if (this.selectedStatus) {
                const statusValue = (element?.dataset?.status || '').trim();
                if (!this.matchesFacetValue(statusValue, this.selectedStatus)) {
                    return false;
                }
            }

            if (this.selectedLocation) {
                const locationValue = (element?.dataset?.location || '').trim();
                if (!this.matchesFacetValue(locationValue, this.selectedLocation)) {
                    return false;
                }
            }

            const q = this.query.trim().toLowerCase();
            if (!q) {
                return true;
            }

            const haystack = element && element.dataset
                ? `${element.dataset.search || ''} ${element.dataset.title || ''} ${element.dataset.summary || ''}`.toLowerCase()
                : '';

            return haystack.includes(q);
        },

        isOutsideTypology(filterArray) {
            if (!filterArray || filterArray.length === 0) {
                return true;
            }

            if (this.activeFilters.includes('all') || this.activeFilters.length === 0) {
                return false;
            }

            return !this.matchesAnyActiveFilter(filterArray);
        },

        matchesAnyActiveFilter(filterArray) {
            for (const activeFilter of this.activeFilters) {
                if (filterArray.includes(activeFilter)) {
                    return true;
                }
                const childFilters = this.filterHierarchy[activeFilter] || [];
                if (childFilters.some(child => filterArray.includes(child))) {
                    return true;
                }
            }
            return false;
        },

        applyDiscovery(options = {}) {
            requestAnimationFrame(() => {
                this.applyDiscoveryNow(options);
            });
        },

        applyDiscoveryNow(options = {}) {
            this.sortItems();
            this.updateItemVisibility();
            this.updateVisibleCount();
            this.hasVisibleItems = this.visibleCount > 0;
            this.updateProjectLinks();
            this.updateURLState();
            if (options.scrollToResults) {
                this.scrollResultsIntoView();
            }
        },

        scrollResultsIntoView() {
            const shell = this.$el.querySelector('.discovery-shell');
            if (!shell) return;

            const shellRect = shell.getBoundingClientRect();
            const isSticky = shellRect.top <= 0 && shellRect.bottom > 0;
            if (!isSticky) {
                return;
            }

            const firstVisibleCard = this.getDiscoveryItems().find(
                (item) => item.dataset.discoveryVisible !== '0'
            );
            if (!firstVisibleCard) return;

            const topGap = 16;
            const desiredCardTop = shellRect.bottom + topGap;
            const getTargetY = () => Math.max(
                0,
                window.scrollY + firstVisibleCard.getBoundingClientRect().top - desiredCardTop
            );

            requestAnimationFrame(() => {
                const targetY = getTargetY();
                if (Math.abs(window.scrollY - targetY) < 2) {
                    return;
                }
                window.scrollTo({
                    top: targetY,
                    behavior: 'smooth',
                });
            });
        },

        getDiscoveryItems() {
            const wrapper = this.cachedElements.get('wrapper');
            if (!wrapper) return [];
            return Array.from(wrapper.querySelectorAll('[data-discovery-item]'));
        },

        updateVisibleCount() {
            const items = this.getDiscoveryItems();
            this.visibleCount = items.filter(item => !item.classList.contains('is-hidden-by-filter')).length;
        },

        updateItemVisibility() {
            const items = this.getDiscoveryItems();
            items.forEach(item => {
                const wasHidden = item.classList.contains('is-hidden-by-filter') || item.dataset.discoveryVisible === '0';
                const isVisible = this.isVisibleByDiscovery(item.dataset.filter || '', item);
                item.classList.toggle('is-hidden-by-filter', !isVisible);
                item.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
                item.style.display = isVisible ? '' : 'none';
                item.dataset.discoveryVisible = isVisible ? '1' : '0';

                if (isVisible && wasHidden) {
                    item.classList.remove('discovery-card-reveal');
                    // Reflow so repeated reveals can retrigger animation.
                    void item.offsetWidth;
                    item.classList.add('discovery-card-reveal');
                    setTimeout(() => item.classList.remove('discovery-card-reveal'), 190);
                }
            });
        },

        sortItems() {
            const wrapper = this.cachedElements.get('wrapper');
            if (!wrapper) return;

            const items = this.getDiscoveryItems();
            if (!items.length) return;

            const sorted = [...items].sort((a, b) => {
                if (this.sortMode === 'az') {
                    const aTitle = (a.dataset.title || a.dataset.search || '').toLowerCase();
                    const bTitle = (b.dataset.title || b.dataset.search || '').toLowerCase();
                    return aTitle.localeCompare(bTitle);
                }

                if (this.sortMode === 'date_desc' || this.sortMode === 'date_asc') {
                    const aDate = Date.parse(a.dataset.created || '') || 0;
                    const bDate = Date.parse(b.dataset.created || '') || 0;
                    if (aDate !== bDate) return this.sortMode === 'date_asc' ? aDate - bDate : bDate - aDate;
                }

                const aIndex = parseInt(a.dataset.originalIndex || '0', 10);
                const bIndex = parseInt(b.dataset.originalIndex || '0', 10);
                return aIndex - bIndex;
            });

            const fragment = document.createDocumentFragment();
            sorted.forEach(item => fragment.appendChild(item));
            wrapper.appendChild(fragment);
        },

        clearSearch() {
            this.query = '';
            this.searchOpen = false;
            this.applyDiscovery({ scrollToResults: true });
        },

        setViewMode(mode) {
            this.viewMode = mode === 'list' ? 'list' : 'grid';
            this.applyDiscovery({ scrollToResults: true });
        },

        setTypologyFilter(filter) {
            const next = (filter || 'all').trim();
            this.selectedTypology = next || 'all';
            this.selectedSubTypology = '';
            this.selectedSubTypologyLabel = 'All';
            this.outsideTypologies = false;
            this.syncActiveFiltersFromTypology();
            this.applyDiscovery({ scrollToResults: true });
        },

        selectTypologyOption(filter, label = '') {
            this.closeDropdown();
            this.selectedTypologyLabel = (label || '').trim() || this.getFilterLabel(filter);
            this.setTypologyFilter(filter);
        },

        setSubTypologyFilter(filter) {
            const selectedFilter = filter || '';
            this.selectedSubTypology = selectedFilter;
            this.outsideTypologies = false;
            this.syncActiveFiltersFromTypology();
            this.applyDiscovery({ scrollToResults: true });
        },

        selectSubTypologyOption(filter, label = '') {
            this.closeDropdown();
            this.selectedSubTypologyLabel = (label || '').trim() || 'All';
            this.setSubTypologyFilter(filter);
        },

        selectStatusOption(value) {
            this.closeDropdown();
            this.selectedStatus = (value || '').trim();
            this.applyDiscovery({ scrollToResults: true });
        },

        selectLocationOption(value) {
            this.closeDropdown();
            this.selectedLocation = (value || '').trim();
            this.applyDiscovery({ scrollToResults: true });
        },

        selectSortOption(value) {
            this.closeDropdown();
            this.sortMode = ['featured', 'date_desc', 'date_asc', 'az'].includes(value) ? value : 'featured';
            this.applyDiscovery({ scrollToResults: true });
        },

        hasResetAvailable() {
            const base = this.initialDiscoveryState;
            if (!base) {
                return false;
            }
            return (
                this.viewMode !== base.viewMode ||
                this.sortMode !== base.sortMode ||
                this.selectedTypology !== base.selectedTypology ||
                this.selectedSubTypology !== base.selectedSubTypology ||
                this.selectedStatus !== base.selectedStatus ||
                this.selectedLocation !== base.selectedLocation ||
                this.query.trim() !== base.query
            );
        },

        resetDiscovery() {
            const base = this.initialDiscoveryState || {
                query: '',
                viewMode: 'grid',
                sortMode: 'featured',
                selectedTypology: 'all',
                selectedSubTypology: '',
                selectedStatus: '',
                selectedLocation: '',
            };

            this.query = base.query;
            this.searchOpen = false;
            this.viewMode = base.viewMode;
            this.sortMode = base.sortMode;
            this.outsideTypologies = false;
            this.selectedTypology = base.selectedTypology;
            this.selectedTypologyLabel = this.getFilterLabel(this.selectedTypology);
            this.selectedSubTypology = base.selectedSubTypology;
            this.selectedSubTypologyLabel = this.selectedSubTypology
                ? this.getFilterLabel(this.selectedSubTypology)
                : 'All';
            this.selectedStatus = base.selectedStatus;
            this.selectedLocation = base.selectedLocation;
            this.syncActiveFiltersFromTypology();
            this.applyDiscovery({ scrollToResults: true });
        },
        
        checkVisibleItems() {
            this.updateVisibleCount();
            this.hasVisibleItems = this.visibleCount > 0;
        },
        
        updateProjectLinks() {
            const wrapper = this.cachedElements.get('wrapper');
            if (!wrapper) return;
            
            const selectors = [
                'a[href*="/work/"]',
                'a[href*="/projects/"]',
                '.project-link',
                '.card a',
                'article a',
                '[class*="filter-"] a'
            ];
            
            let projectLinks = [];
            
            for (const selector of selectors) {
                const found = wrapper.querySelectorAll(selector);
                if (found.length > 0) {
                    projectLinks = Array.from(found);
                    break;
                }
            }
            
            if (projectLinks.length === 0) return;
            
            // Batch link updates
            const filterParam = this.buildFilterParam();
            
            projectLinks.forEach(link => {
                const originalHref = link.getAttribute('data-original-href') || link.href;
                
                if (!link.getAttribute('data-original-href')) {
                    link.setAttribute('data-original-href', originalHref);
                }
                
                if (filterParam) {
                    const separator = originalHref.includes('?') ? '&' : '?';
                    link.href = `${originalHref}${separator}${filterParam}`;
                } else {
                    link.href = originalHref;
                }
            });
        },
        
        buildFilterParam() {
            if (this.activeFilters.length === 0 || this.activeFilters.includes('all')) {
                if (
                    !this.query &&
                    !this.outsideTypologies &&
                    this.sortMode === 'featured' &&
                    this.viewMode === 'grid' &&
                    !this.selectedStatus &&
                    !this.selectedLocation
                ) {
                    return '';
                }
            }
            
            const params = new URLSearchParams();
            if (this.activeFilters.length > 0 && !this.activeFilters.includes('all')) {
                params.set('filters', this.activeFilters.join(','));
            }
            
            if (this.expandedParents.size > 0) {
                params.set('expanded', Array.from(this.expandedParents).join(','));
            }
            
            if (this.isCumulative) {
                params.set('cumulative', '1');
            }

            if (this.query.trim()) {
                params.set('q', this.query.trim());
            }

            if (this.viewMode !== 'grid') {
                params.set('view', this.viewMode);
            }

            if (this.sortMode !== 'featured') {
                params.set('sort', this.sortMode);
            }

            if (this.outsideTypologies) {
                params.set('outside', '1');
            }

            if (this.selectedStatus) {
                params.set('status', this.selectedStatus);
            }

            if (this.selectedLocation) {
                params.set('location', this.selectedLocation);
            }
            
            return params.toString();
        },
        
        clearAllFilters() {
            this.setFilter('all');
        },
        
        hasActiveChildren(parentFilter) {
            const children = this.filterHierarchy[parentFilter];
            return children?.some(childFilter => this.activeFilters.includes(childFilter)) || false;
        },
        
        getActiveChildCount(parentFilter) {
            const children = this.filterHierarchy[parentFilter];
            return children?.filter(childFilter => this.activeFilters.includes(childFilter)).length || 0;
        },
        
        updateActiveFilterClasses() {
            const filters = this.cachedElements.get('filters');
            const filterParents = this.cachedElements.get('filterParents');

            // Child / standalone filters (elements with `.filter`)
            filters.forEach(el => {
                const token = el.dataset.filter;
                const parent = this.parentMap ? this.parentMap[token] : null;
                const isSelected = this.activeFilters.includes(token) || (parent && this.activeFilters.includes(parent));
                el.classList.toggle("filter-active", isSelected);
                el.setAttribute("aria-pressed", isSelected);
            });

            // Parent pills (elements with `.filter-parent`)
            filterParents.forEach(el => {
                const token = el.dataset.filter;
                const children = this.filterHierarchy[token] || [];
                const isSelected = this.activeFilters.includes(token);
                const hasActiveChildren = children.some(child => this.activeFilters.includes(child));
                const isActive = isSelected || hasActiveChildren;

                el.classList.toggle("filter-active", isActive);
                el.classList.toggle("has-active-children", hasActiveChildren);
                el.setAttribute("aria-pressed", isActive);
            });
        },
        
        updateExpandedStates() {
            const filterParents = this.cachedElements.get('filterParents');
            const filterChildren = this.cachedElements.get('filterChildren');

            // Batch parent updates
            filterParents.forEach(el => {
                const filter = el.dataset.filter;
                const isExpanded = this.isParentExpanded(filter);
                el.classList.toggle("expanded", isExpanded);
                el.setAttribute("aria-expanded", isExpanded);
            });

            // Batch children updates
            filterChildren.forEach(el => {
                const parentFilter = el.dataset.parent;
                const isExpanded = this.isParentExpanded(parentFilter);

                el.classList.toggle('expanded', isExpanded);
                el.setAttribute("aria-hidden", !isExpanded);

                if (isExpanded) {
                    el.style.display = "block";
                    el.classList.remove('hide');
                    el.offsetHeight; // Trigger reflow
                    el.classList.add('show');
                } else {
                    el.classList.remove('show');
                    el.classList.add('hide');
                    setTimeout(() => {
                        if (!this.expandedParents.has(parentFilter)) {
                            el.style.display = "none";
                        }
                    }, 300);
                }
            });
        },

        syncTypologyFromActiveFilters() {
            const selected = this.activeFilters.find(f => f !== 'all') || 'all';
            const parent = this.parentMap[selected] || selected;
            this.selectedTypology = parent;
            this.selectedTypologyLabel = this.getFilterLabel(parent);
            this.selectedSubTypology = this.parentMap[selected] ? selected : '';
            if (this.selectedSubTypology) {
                const child = this.getChildTypologyOptions().find((opt) => opt.value === this.selectedSubTypology);
                this.selectedSubTypologyLabel = child?.label || this.getFilterLabel(this.selectedSubTypology);
            } else {
                this.selectedSubTypologyLabel = 'All';
            }
        },

        hydrateFacetOptions() {
            const items = this.getDiscoveryItems();
            const statuses = new Set();
            const locations = new Set();

            items.forEach(item => {
                const status = (item.dataset.status || '').trim();
                const location = (item.dataset.location || '').trim();

                if (status) {
                    statuses.add(status);
                }

                if (location) {
                    locations.add(location);
                }
            });

            this.statusOptions = Array.from(statuses).sort((a, b) => a.localeCompare(b));
            this.locationOptions = Array.from(locations).sort((a, b) => a.localeCompare(b));

            if (this.selectedStatus && !this.statusOptions.includes(this.selectedStatus)) {
                this.selectedStatus = '';
            }

            if (this.selectedLocation && !this.locationOptions.includes(this.selectedLocation)) {
                this.selectedLocation = '';
            }
        },

        getChildTypologyOptions() {
            if (!this.selectedTypology || this.selectedTypology === 'all') {
                return [];
            }

            const mappedChildren = this.childTypologyOptionsByParent[this.selectedTypology];
            if (Array.isArray(mappedChildren) && mappedChildren.length > 0) {
                return mappedChildren.map((item) => ({
                    value: item.value,
                    label: item.label || item.value,
                }));
            }

            const children = this.filterHierarchy[this.selectedTypology] || [];
            return children.map((token) => ({
                value: token,
                label: this.filterLabels[token] || token,
            }));
        },

        getFilterLabel(token) {
            if (!token || token === 'all') {
                return 'All';
            }
            // Prefer child option labels if available
            for (const parent in this.childTypologyOptionsByParent) {
                const found = (this.childTypologyOptionsByParent[parent] || []).find((opt) => opt.value === token);
                if (found?.label) {
                    return found.label;
                }
            }
            const optionInDOM = this.$el
                ? this.$el.querySelector(`.discovery-dropdown__option[data-filter-token="${token}"]`)
                : null;
            if (optionInDOM && optionInDOM.textContent) {
                const domLabel = optionInDOM.textContent.trim();
                if (domLabel) {
                    return domLabel;
                }
            }
            return this.filterLabels[token] || token;
        },

        getTypologyLabel(token) {
            return this.getFilterLabel(token);
        },

        getSortLabel(mode) {
            const map = {
                date_desc: 'Date (Newest first)',
                date_asc: 'Date (Oldest first)',
                featured: 'Featured',
                az: 'A-Z',
            };
            return map[mode] || 'Featured';
        },

        matchesSelectedTypology(filterArray) {
            if (!Array.isArray(filterArray) || !filterArray.length) {
                return this.selectedTypology === 'all' && !this.selectedSubTypology;
            }

            if (this.selectedSubTypology) {
                return filterArray.includes(this.selectedSubTypology);
            }

            if (!this.selectedTypology || this.selectedTypology === 'all') {
                return true;
            }

            return filterArray.includes(this.selectedTypology);
        },

        normalizeFacetValue(value) {
            return (value || '')
                .toString()
                .toLowerCase()
                .replace(/\s+/g, ' ')
                .trim();
        },

        matchesFacetValue(itemValue, selectedValue) {
            const selected = this.normalizeFacetValue(selectedValue);
            if (!selected) {
                return true;
            }

            const raw = (itemValue || '').toString();
            if (!raw.trim()) {
                return false;
            }

            const options = raw
                .split(/[;,|]/)
                .map((part) => this.normalizeFacetValue(part))
                .filter(Boolean);

            if (!options.length) {
                return false;
            }

            return options.some((option) => option === selected || option.includes(selected) || selected.includes(option));
        },

        syncActiveFiltersFromTypology() {
            this.expandedParents.clear();
            if (this.selectedSubTypology) {
                this.activeFilters = [this.selectedSubTypology];
                return;
            }

            this.activeFilters = this.selectedTypology === 'all'
                ? ['all']
                : [this.selectedTypology];
        },

        captureInitialDiscoveryState() {
            this.initialDiscoveryState = {
                query: this.query.trim(),
                viewMode: this.viewMode,
                sortMode: this.sortMode,
                selectedTypology: this.selectedTypology,
                selectedSubTypology: this.selectedSubTypology,
                selectedStatus: this.selectedStatus,
                selectedLocation: this.selectedLocation,
            };
        }
    };
}
